import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Modal from 'react-responsive-modal';
import LANGUAGES_CONTROLLER from '../../controllers/languages';
import { getDirectSubmissionEnabledCountries } from '../../utils/autoSubmissionConfig';
import { WILDCARD, DS_MAX_COMPANIES_PER_USER } from './constants';

const MODAL_CLASSNAMES = {
    modal: 'ds-scope-modal'
};

const ALL_COUNTRIES = WILDCARD;

/**
 * Direct submission is only available in a subset of countries, so the picker offers only those -
 * a company's other registered countries are not selectable scope here.
 */
const toDirectSubmissionCountries = (countries) => {
    const enabledCountries = getDirectSubmissionEnabledCountries();
    return (countries || []).filter(countryCode =>
        enabledCountries.includes(String(countryCode).toUpperCase())
    );
};

const getSelectedSummary = (selections) => {
    let companyCount = 0;
    let countryCount = 0;

    Object.values(selections).forEach(company => {
        const selected = Object.values(company.countries).filter(Boolean).length;
        if (selected > 0) {
            companyCount += 1;
            countryCount += selected;
        }
    });

    return { companyCount, countryCount };
};

const getScopedCompanyIds = (initialScope) => {
    if (!initialScope) return [];
    if (initialScope.kind === 'selections') {
        return Object.keys(initialScope.value);
    }
    if (initialScope.value?.isDefault) return [];
    return [...new Set((initialScope.value?.pairs || []).map(pair => String(pair.companyId)))];
};

const getAssignedCompanyIds = (initialScope) => new Set(
    getScopedCompanyIds(initialScope).filter(companyId =>
        initialScope.kind !== 'selections'
        || Object.values(initialScope.value[companyId]?.countries || {}).some(Boolean))
);

const buildCompanyInitialCountries = (company, initialScope) => {
    const base = company.countries.reduce((acc, code) => { acc[code] = false; return acc; }, {});
    if (!initialScope) return base;

    if (initialScope.kind === 'selections') {
        const saved = initialScope.value[company.id]?.countries || {};
        Object.keys(base).forEach(code => { if (saved[code]) base[code] = true; });
        return base;
    }

    (initialScope.value?.pairs || [])
        .filter(pair => String(pair.companyId) === String(company.id))
        .forEach(pair => {
            if (pair.countryCode === ALL_COUNTRIES) {
                Object.keys(base).forEach(code => { base[code] = true; });
            } else if (base[pair.countryCode] !== undefined) {
                base[pair.countryCode] = true;
            }
        });
    return base;
};

const mergeScopeSelections = (prev, enriched, initialScope) => {
    const next = { ...prev };
    enriched.forEach(company => {
        if (!company.countries || next[company.id]) return;
        next[company.id] = { countries: buildCompanyInitialCountries(company, initialScope) };
    });
    return next;
};

const CompanyExpandedContent = ({ company, companySelections, onToggleCountry, onToggleCompany, languages }) => {
    const isLoaded = !!company.countries;
    const countries = company.countries || [];
    const selectedCount = Object.values(companySelections).filter(Boolean).length;
    const allSelected = countries.length > 0 && selectedCount === countries.length;
    const someSelected = selectedCount > 0 && selectedCount < countries.length;

    if (company.error) {
        return (
            <p className="small-font ml30 mb10 text-red">
                {LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_LoadCompanyError')}
            </p>
        );
    }

    if (!isLoaded || company.loading) {
        return (
            <p className="small-font ml30 mb10">
                {LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_LoadingCompanyDetails')}
            </p>
        );
    }

    if (countries.length === 0) {
        return (
            <p className="small-font ml30 m4" data-testid={`no-enabled-countries-${company.id}`}>
                {LANGUAGES_CONTROLLER.format(
                    languages,
                    'DSPermissions_NoEnabledCountries',
                    [getDirectSubmissionEnabledCountries().join(', ')]
                )}
            </p>
        );
    }

    return (
        <div className="ml30 mb10">
            <fieldset className="mt10 mb5">
                <input
                    type="checkbox"
                    id={`select-all-countries-${company.id}`}
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected; }}
                    onChange={(e) => onToggleCompany(company.id, e.target.checked)}
                    data-testid={`select-all-countries-${company.id}`}
                />
                <label className="checkbox" htmlFor={`select-all-countries-${company.id}`}>
                    {LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_SelectAllCountries')}
                </label>
            </fieldset>

            <table>
                <thead>
                    <tr>
                        <th className="pt0 pb0">
                            {LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_Country')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {countries.map(country => (
                        <tr key={`${company.id}-${country}`}>
                            <td>
                                <fieldset>
                                    <input
                                        type="checkbox"
                                        id={`country-${company.id}-${country}`}
                                        checked={!!companySelections[country]}
                                        onChange={(e) => onToggleCountry(company.id, country, e.target.checked)}
                                        data-testid={`country-checkbox-${company.id}-${country}`}
                                    />
                                    <label className="checkbox" htmlFor={`country-${company.id}-${country}`}>
                                        {country}
                                    </label>
                                </fieldset>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};


const CompanySection = ({ company, selections, onToggleCountry, onToggleCompany, onExpand, languages }) => {
    const [expanded, setExpanded] = useState(false);
    const companySelections = selections[company.id]?.countries || {};
    const countries = company.countries || [];
    const selectedCount = Object.values(companySelections).filter(Boolean).length;
    const isLoaded = !!company.countries;

    const handleExpand = () => {
        const willExpand = !expanded;
        setExpanded(willExpand);
        if (willExpand && !isLoaded && !company.loading) {
            onExpand(company.id);
        }
    };

    return (
        <>
            <tr>
                <td>
                    <s-icon
                        name={expanded ? 'chevron-down' : 'chevron-right'}
                        className="mr5"
                        onClick={handleExpand}
                        aria-expanded={expanded}
                        data-testid={`toggle-company-${company.id}`}
                    />
                    <span className="bold">{company.name}</span>
                </td>
                <td className="text-align-right small-font" style={{ whiteSpace: 'nowrap' }}>
                    {isLoaded && (selectedCount > 0
                        ? countries.filter(code => companySelections[code]).join(', ')
                        : `${selectedCount}/${countries.length}`)}
                </td>
            </tr>
            {expanded && (
                <tr>
                    <td colSpan={2} className="p0">
                        <CompanyExpandedContent
                            company={company}
                            companySelections={companySelections}
                            onToggleCountry={onToggleCountry}
                            onToggleCompany={onToggleCompany}
                            languages={languages}
                        />
                    </td>
                </tr>
            )}
        </>
    );
};


const AssignCompaniesModal = (props) => {
    const { open, onClose, onApply, user, userCompanies, onLoadCompanyVatNumbers, companyVatNumbers, initialScope, languages } = props;

    const [selections, setSelections] = useState({});
    const [enrichedCompanies, setEnrichedCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    const rebuildEnriched = useCallback((companies, vatCache) => {
        return companies.map(company => {
            const vatData = vatCache?.[company.id];
            if (!vatData) return { ...company, countries: null, loading: false };

            return { ...company, countries: toDirectSubmissionCountries(vatData.countries) };
        });
    }, []);

    const scopedCompanyIds = useMemo(() => getScopedCompanyIds(initialScope), [initialScope]);

    // Reset selections and pre-load VAT for scoped companies whenever the modal opens for a user.
    useEffect(() => {
        if (!open) return;
        setSearchTerm('');
        setSelections({});
        if (scopedCompanyIds.length) {
            onLoadCompanyVatNumbers(scopedCompanyIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, user?.id]);

    // Build enriched companies and initialize selections, pre-checking any existing scope.
    useEffect(() => {
        if (!open || !userCompanies?.length) return;
        const enriched = rebuildEnriched(userCompanies, companyVatNumbers);
        setEnrichedCompanies(enriched);
        setSelections(prev => mergeScopeSelections(prev, enriched, initialScope));
    }, [open, userCompanies, companyVatNumbers, initialScope, rebuildEnriched]);

    const handleExpandCompany = async (companyId) => {
        if (companyVatNumbers?.[companyId]) return;

        setEnrichedCompanies(prev =>
            prev.map(c => c.id === companyId ? { ...c, loading: true, error: false } : c)
        );

        try {
            await onLoadCompanyVatNumbers([companyId]);
        } catch {
            setEnrichedCompanies(prev =>
                prev.map(c => c.id === companyId ? { ...c, loading: false, error: true } : c)
            );
        }
    };

    const filteredCompanies = useMemo(() => {
        if (!searchTerm.trim()) return enrichedCompanies;
        const term = searchTerm.toLowerCase();
        return enrichedCompanies.filter(c => c.name.toLowerCase().includes(term));
    }, [enrichedCompanies, searchTerm]);

    const assignedCompanyIds = useMemo(() => getAssignedCompanyIds(initialScope), [initialScope]);

    const { assignedCompanies, availableCompanies } = useMemo(() => {
        const assigned = [];
        const available = [];
        filteredCompanies.forEach(company => {
            const isAssigned = assignedCompanyIds.has(String(company.id))
                && (!company.countries || Object.values(buildCompanyInitialCountries(company, initialScope)).some(Boolean));
            (isAssigned ? assigned : available).push(company);
        });
        return { assignedCompanies: assigned, availableCompanies: available };
    }, [filteredCompanies, assignedCompanyIds, initialScope]);

    const summary = useMemo(() => getSelectedSummary(selections), [selections]);
    const isOverLimit = summary.companyCount > DS_MAX_COMPANIES_PER_USER;

    const handleToggleCountry = (companyId, country, checked) => {
        setSelections(prev => ({
            ...prev,
            [companyId]: {
                ...prev[companyId],
                countries: { ...prev[companyId].countries, [country]: checked }
            }
        }));
    };

    const handleToggleCompany = (companyId, checked) => {
        setSelections(prev => {
            const current = prev[companyId];
            if (!current) return prev;
            const updated = { ...current.countries };
            Object.keys(updated).forEach(country => { updated[country] = checked; });
            return { ...prev, [companyId]: { ...current, countries: updated } };
        });
    };

    const handleApply = () => {
        if (isOverLimit) return;
        onApply(user.id, selections);
    };

    const renderCompany = company => (
        <CompanySection
            key={company.id}
            company={company}
            selections={selections}
            onToggleCountry={handleToggleCountry}
            onToggleCompany={handleToggleCompany}
            onExpand={handleExpandCompany}
            languages={languages}
        />
    );

    const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
    const summaryText = `${summary.companyCount} ${LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_CompaniesCount')}, ${summary.countryCount} ${LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_CountriesSelected')}`;
    const limitErrorText = isOverLimit
        ? LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_ScopeLimitExceeded', [
            summary.companyCount,
            DS_MAX_COMPANIES_PER_USER,
            summary.companyCount - DS_MAX_COMPANIES_PER_USER
        ])
        : null;

    return (
        <Modal
            classNames={MODAL_CLASSNAMES}
            open={open}
            onClose={onClose}
            center
        >
            <h2 className="mt0 mb5">
                {LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_AssignModalTitle')}
            </h2>
            <p className="small-font mb10">{userName}</p>

            <input
                type="text"
                className="mb15"
                placeholder={LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_SearchCompany')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="company-search-input"
            />

            <div className="ds-scope-modal-list">
                <s-table-container data-testid="ds-scope-table" tabindex="0">
                    <table>
                        {assignedCompanies.length > 0 && (
                            <tbody data-testid="ds-scope-assigned">
                                <tr>
                                    <th colSpan={2} scope="colgroup" className="ds-scope-section-header">
                                        {LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_AssignedCompaniesSection')}
                                    </th>
                                </tr>
                                {assignedCompanies.map(renderCompany)}
                            </tbody>
                        )}
                        <tbody data-testid="ds-scope-available">
                            {assignedCompanies.length > 0 && availableCompanies.length > 0 && (
                                <tr>
                                    <th colSpan={2} scope="colgroup" className="ds-scope-section-header">
                                        {LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_AvailableCompaniesSection')}
                                    </th>
                                </tr>
                            )}
                            {availableCompanies.map(renderCompany)}
                        </tbody>
                    </table>
                </s-table-container>
            </div>

            {isOverLimit && (
                <s-alert
                    id="ds-scope-limit-error"
                    status="error"
                    role="alert"
                    nodismiss={true}
                    class="mt10"
                    data-testid="ds-scope-limit-error"
                >
                    <span>{limitErrorText}</span>
                </s-alert>
            )}

            <div className="bottom-nav-panel mt10 flex align-items-center justify-content-space">
                <span className="small-font" data-testid="ds-scope-limit-hint">
                    {LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_ScopeLimitHint', [DS_MAX_COMPANIES_PER_USER])}
                </span>
                <div>
                    <span className="small-font mr15">{summaryText}</span>
                    <button
                        className="secondary mr10"
                        onClick={onClose}
                        data-testid="assign-modal-cancel"
                    >
                        {LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_Cancel')}
                    </button>
                    <button
                        className="primary"
                        onClick={handleApply}
                        disabled={isOverLimit}
                        title={limitErrorText || undefined}
                        aria-describedby={isOverLimit ? 'ds-scope-limit-error' : undefined}
                        data-testid="assign-modal-apply"
                    >
                        {LANGUAGES_CONTROLLER.format(languages, 'DSPermissions_Confirm')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};


export default AssignCompaniesModal;
